// A dropdown field's control in its drawer: the options not yet applied, in the order defined on the field,
// then Is empty, as rows like the panel's dropdown menus. Picking one moves it to the drawer's Applied list.
import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import type {CustomFieldColumnDef} from '../custom-field-columns/customFieldColumnModel';
import {EMPTY_OPTION} from './customFieldFilterConditions';
import {MenuRow, Muted, PANEL_FONT_SIZE, isPressKey} from './customFieldFilter.styled';

const Options = Styled.div`
	padding: 4px 0 6px;
`;

const AllApplied = Styled(Muted)`
	display: block;
	padding: 7px 12px;
	font-size: ${PANEL_FONT_SIZE};
`;

export interface CustomFieldDropdownOptionsProps {
	definition: CustomFieldColumnDef;
	applied: unknown[];
	onApply: (values: unknown[]) => void;
}

export const CustomFieldDropdownOptions = ({definition, applied, onApply}: CustomFieldDropdownOptionsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const cy = `cf-filter-${definition.key}`;
	const remaining = [
		...definition.options.map(option => ({value: option.id as unknown, label: option.displayName})),
		{value: EMPTY_OPTION as unknown, label: formatMessage({id: 'filters.custom_field.row.is_empty'})},
	].filter(option => !applied.includes(option.value));

	if (remaining.length === 0) {
		return (
			<Options>
				<AllApplied data-cy={`${cy}-all-applied`}>
					{formatMessage({id: 'filters.custom_field.all_options_applied'})}
				</AllApplied>
			</Options>
		);
	}
	return (
		<Options>
			{remaining.map(option => {
				const pick = () => onApply([...applied, option.value]);
				return (
					<MenuRow
						key={String(option.value)}
						role="button"
						tabIndex={0}
						data-cy={`${cy}-option`}
						onClick={pick}
						onKeyDown={(event: React.KeyboardEvent) => {
							if (!isPressKey(event.key)) return;
							event.preventDefault();
							pick();
						}}
					>
						{option.label}
					</MenuRow>
				);
			})}
		</Options>
	);
};
